import { Injectable } from '@angular/core';
import { AgendaEvent } from '@cropx-common/models/CalendarEventsModel';
import { FieldSummaryContent } from '@cropx-common/models/FieldDetailsModel';
import { DailyWeatherEntry } from '@cropx-common/services/api-gateway/WeatherApiService';
import { EventHelper } from '@cropx-web/screens/field/field-agenda/event-utils';
import { AgendaSelectors } from '@cropx-web/screens/field/state/agenda.state';
import { Record as CropRecord } from '@cropx-web/services/api-gateway/CropRecordingApiService';
import {
  Action,
  Selector,
  State,
  StateContext,
  createPropertySelectors,
} from '@ngxs/store';
import dayjs from 'dayjs';

import { FieldWeatherSelectors } from '../../state/field-weather.state';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace FieldAgendaPage {
  const type = 'FieldAgendaPage';

  export class SetActiveEventId {
    static readonly type = `[${type}] SetActiveEventId`;

    constructor(
      public activeEventId: number,
      public activeDate: string,
    ) {}
  }

  export class SetActiveRecord {
    static readonly type = `[${type}] SetActiveRecord`;

    constructor(
      public activeRecordUuid: string,
      public activeDate: string,
    ) {}
  }

  export class SetActiveDay {
    static readonly type = `[${type}] SetActiveDay`;

    constructor(public startOfDay: number) {}
  }

  export class SetActiveDate {
    static readonly type = `[${type}] SetActiveDate`;

    constructor(public activeDate: string) {}
  }

  export class ClearActiveEvents {
    static readonly type = `[${type}] ClearActiveEvents`;
  }

  export class EditEvent {
    static readonly type = `[${type}] EditEvent`;

    constructor(public event: AgendaEvent) {}
  }

  export class ClearSelectedEvent {
    static readonly type = `[${type}] ClearSelectedEvent`;
  }

  export class SetSelectedField {
    static readonly type = `[${type}] SetSelectedField`;

    constructor(public field: FieldSummaryContent) {}
  }

  export class SetSearchPanelVisibility {
    static readonly type = `[${type}] SetSearchPanelVisibility`;

    constructor(public isPanelOpen: boolean) {}
  }

  export class SearchEvents {
    static readonly type = `[${type}] SearchEvents`;

    constructor(public searchValue: string) {}
  }
}

export interface FieldAgendaPageStateModel {
  activeDate: string | null;
  activeEventId: number | null;
  activeRecordUuid: string | null;
  selectedEvent: AgendaEvent | null;
  isPanelOpen: boolean;
  searchValue: string;
}

@State<FieldAgendaPageStateModel>({
  name: 'fieldAgendaPage',
  defaults: {
    activeDate: null,
    activeEventId: null,
    activeRecordUuid: null,
    selectedEvent: null,
    isPanelOpen: false,
    searchValue: '',
  },
})
@Injectable()
export class FieldAgendaPageState {
  @Action(FieldAgendaPage.SetActiveEventId)
  setActiveEventId(
    ctx: StateContext<FieldAgendaPageStateModel>,
    action: FieldAgendaPage.SetActiveEventId,
  ): void {
    ctx.patchState({
      activeEventId: action.activeEventId,
      activeRecordUuid: null,
      activeDate: action.activeDate,
      selectedEvent: null,
    });
  }

  @Action(FieldAgendaPage.SetActiveRecord)
  setActiveRecord(
    ctx: StateContext<FieldAgendaPageStateModel>,
    action: FieldAgendaPage.SetActiveRecord,
  ): void {
    ctx.patchState({
      activeEventId: null,
      activeRecordUuid: action.activeRecordUuid,
      activeDate: action.activeDate,
      selectedEvent: null,
    });
  }

  @Action(FieldAgendaPage.SetActiveDay)
  setActiveDay(ctx: StateContext<FieldAgendaPageStateModel>): void {
    ctx.patchState({
      activeRecordUuid: null,
      activeEventId: null,
      selectedEvent: null,
    });
  }

  @Action(FieldAgendaPage.SetActiveDate)
  setActiveDate(
    ctx: StateContext<FieldAgendaPageStateModel>,
    action: FieldAgendaPage.SetActiveDate,
  ): void {
    ctx.patchState({
      activeDate: action.activeDate,
      activeRecordUuid: null,
      activeEventId: null,
      selectedEvent: null,
    });
  }

  @Action(FieldAgendaPage.ClearActiveEvents)
  clearActiveEvents(ctx: StateContext<FieldAgendaPageStateModel>): void {
    ctx.patchState({
      activeDate: null,
      activeEventId: null,
      activeRecordUuid: null,
      selectedEvent: null,
    });
  }

  @Action(FieldAgendaPage.EditEvent)
  editEvent(
    ctx: StateContext<FieldAgendaPageStateModel>,
    action: FieldAgendaPage.EditEvent,
  ): void {
    ctx.patchState({
      selectedEvent: action.event,
    });
  }

  @Action(FieldAgendaPage.ClearSelectedEvent)
  clearSelectedEvent(ctx: StateContext<FieldAgendaPageStateModel>): void {
    ctx.patchState({
      selectedEvent: null,
    });
  }

  @Action(FieldAgendaPage.SetSearchPanelVisibility)
  setSearchPanelVisibility(
    ctx: StateContext<FieldAgendaPageStateModel>,
    action: FieldAgendaPage.SetSearchPanelVisibility,
  ): void {
    ctx.patchState({
      isPanelOpen: action.isPanelOpen,
    });
  }

  @Action(FieldAgendaPage.SearchEvents)
  searchEvents(
    ctx: StateContext<FieldAgendaPageStateModel>,
    action: FieldAgendaPage.SearchEvents,
  ): void {
    ctx.patchState({
      searchValue: action.searchValue,
    });
  }
}

export class FieldAgendaPageSelectors {
  static slices =
    createPropertySelectors<FieldAgendaPageStateModel>(FieldAgendaPageState);

  @Selector([
    FieldWeatherSelectors.slices.dailyWeather,
    FieldAgendaPageSelectors.slices.activeDate,
  ])
  static dayWeather(
    daily: DailyWeatherEntry[],
    activeDate: string,
  ): DailyWeatherEntry {
    return daily.find((d) => d.day === activeDate);
  }

  @Selector([
    FieldAgendaPageSelectors.slices.activeEventId,
    FieldAgendaPageSelectors.slices.activeDate,
  ])
  static panelOpen(activeEventId: number, activeDate: string): boolean {
    return !!activeEventId || !!activeDate;
  }

  @Selector([FieldAgendaPageState])
  static searchPanelVisibility(state: FieldAgendaPageStateModel): boolean {
    return state.isPanelOpen;
  }

  @Selector([
    FieldAgendaPageSelectors.slices.searchValue,
    AgendaSelectors.eventsFiltered,
  ])
  static searchEvents(
    searchValue: string,
    events: AgendaEvent[],
  ): AgendaEvent[] {
    return events
      .filter((e) =>
        e.description
          .toLocaleLowerCase()
          .includes(searchValue?.toLocaleLowerCase()),
      )
      .sort((rec1, rec2) => {
        return rec2.eventTime - rec1.eventTime;
      });
  }

  @Selector([FieldAgendaPageSelectors.searchEvents])
  static groupedSearchedEvents(
    events: AgendaEvent[],
  ): Record<number, AgendaEvent> {
    const groupedListByEventTime: Record<number, AgendaEvent> = events.reduce(
      function (a, e) {
        const d = new Date(e.eventTime);
        d.setUTCHours(0, 0, 0, 0);

        const estKey = d.getTime();

        (a[estKey] ? a[estKey] : (a[estKey] = [])).push(e);
        return a;
      },
      {},
    );

    return groupedListByEventTime;
  }

  @Selector([FieldAgendaPageState])
  static selectedEvent(state: FieldAgendaPageStateModel): AgendaEvent {
    return state.selectedEvent;
  }

  @Selector([
    FieldAgendaPageSelectors.slices.activeDate,
    AgendaSelectors.eventsFiltered,
  ])
  static activeEvents(
    activeDate: string,
    events: AgendaEvent[],
  ): AgendaEvent[] {
    if (!activeDate) {
      return [];
    }
    const activeDateTimestamp = dayjs(activeDate, 'YYYY-MM-DD').valueOf();

    const filtered = events
      .filter((e) => {
        if (!e.startTime || !e.endTime) {
          return EventHelper.getEventTimeAsString(e.eventTime) === activeDate;
        }

        if (
          e.startTime <= activeDateTimestamp &&
          e.endTime >= activeDateTimestamp
        ) {
          return true;
        }

        if (
          dayjs(e.endTime).format('YYYY-MM-DD') === activeDate ||
          dayjs(e.startTime).format('YYYY-MM-DD') === activeDate
        ) {
          return true;
        }
        return false;
      })
      .sort((rec1, rec2) => {
        if (rec1?.startTime === null || rec2?.startTime === null) {
          return rec2.eventTime - rec1.eventTime;
        }
        return rec2.startTime - rec1.startTime;
      });
    return filtered;
  }

  @Selector([
    FieldAgendaPageSelectors.slices.activeDate,
    AgendaSelectors.slices.records,
  ])
  static activeRecords(
    activeDate: string,
    records: CropRecord[],
  ): CropRecord[] {
    if (!activeDate) {
      return [];
    }
    return records.filter(
      (r) => dayjs(r.date).format('YYYY-MM-DD') === activeDate,
    );
  }
}
